import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['modal', 'video', 'canvas', 'fileInput', 'fallbackInput', 'error']

  connect () {
    this.stream = null
    this.facingMode = 'environment'
    this._onTurboBeforeCache = () => { this.close() }
    document.addEventListener('turbo:before-cache', this._onTurboBeforeCache)
  }

  disconnect () {
    document.removeEventListener('turbo:before-cache', this._onTurboBeforeCache)
    this.stopStream()
  }

  supportsCamera () {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  }

  async open (event) {
    event.preventDefault()

    if (!this.supportsCamera()) {
      this.openFallback()
      return
    }

    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('flex')
    await this.startStream()
  }

  openFallback () {
    this.fallbackInputTarget.click()
  }

  async startStream () {
    this.clearError()

    try {
      this.stopStream()
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: this.facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      })
      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
    } catch (_err) {
      this.showError('Camera unavailable. Try gallery attach or allow camera permission.')
      this.close()
      this.openFallback()
    }
  }

  stopStream () {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    if (this.hasVideoTarget) {
      this.videoTarget.srcObject = null
    }
  }

  close (event) {
    event?.preventDefault()
    this.stopStream()
    this.modalTarget.classList.add('hidden')
    this.modalTarget.classList.remove('flex')
    this.clearError()
  }

  backdropClose (event) {
    if (event.target === this.modalTarget) this.close(event)
  }

  stopPropagation (event) {
    event.stopPropagation()
  }

  async switchCamera (event) {
    event.preventDefault()
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment'
    await this.startStream()
  }

  capture (event) {
    event.preventDefault()

    const video = this.videoTarget
    const canvas = this.canvasTarget
    const width = video.videoWidth
    const height = video.videoHeight

    if (!width || !height) return

    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, width, height)

    canvas.toBlob((blob) => {
      if (!blob) return

      const file = new File([blob], `chat-photo-${Date.now()}.jpg`, { type: 'image/jpeg' })
      this.assignFile(file)
      this.close()
      this.element.dispatchEvent(new CustomEvent('chat-camera:captured', { bubbles: true }))
    }, 'image/jpeg', 0.88)
  }

  fallbackSelected (event) {
    const file = event.target.files?.[0]
    if (!file) return

    this.assignFile(file)
    event.target.value = ''
    this.element.dispatchEvent(new CustomEvent('chat-camera:captured', { bubbles: true }))
  }

  assignFile (file) {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.fileInputTarget.files = transfer.files
    this.fileInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  showError (message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  clearError () {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }
}
